/**
 * Synthesized sound effects via WebAudio. No asset files. Respects
 * settings.sound / settings.volume. Silent no-op wherever audio is unavailable.
 */
import { useSettings } from '@/store/settings';

export type SoundId =
  | 'roll'
  | 'land'
  | 'hold'
  | 'unhold'
  | 'score'
  | 'scratch'
  | 'bonus'
  | 'jackpot'
  | 'click'
  | 'turn'
  | 'win'
  | 'lose'
  | 'power';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext })
        .AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.connect(ctx.destination);
  } catch {
    ctx = null;
  }
  return ctx;
}

function level(): number {
  const s = useSettings.getState();
  return s.sound ? s.volume : 0;
}

function getNoise(c: AudioContext): AudioBuffer {
  if (noiseBuffer) return noiseBuffer;
  const len = c.sampleRate * 1.5;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buf;
  return buf;
}

interface ToneOpts {
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  glideTo?: number;
  at?: number;
}

function tone(c: AudioContext, freq: number, dur: number, opts: ToneOpts = {}) {
  if (!master) return;
  const { type = 'sine', gain = 0.25, attack = 0.005, glideTo, at = 0 } = opts;
  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noise(
  c: AudioContext,
  dur: number,
  filterHz: number,
  gain = 0.3,
  at = 0,
  q = 1
) {
  if (!master) return;
  const t0 = c.currentTime + at;
  const src = c.createBufferSource();
  src.buffer = getNoise(c);
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = filterHz;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(g).connect(master);
  src.start(t0, Math.random());
  src.stop(t0 + dur + 0.02);
}

const N = {
  C4: 261.63,
  E4: 329.63,
  G4: 392.0,
  A4: 440.0,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  G5: 783.99,
  A5: 880.0,
  C6: 1046.5,
  E6: 1318.5,
};

const PLAY: Record<SoundId, (c: AudioContext) => void> = {
  roll: c => {
    for (let i = 0; i < 7; i++) {
      noise(
        c,
        0.06,
        1800 + Math.random() * 1200,
        0.18,
        i * 0.045 + Math.random() * 0.02,
        2
      );
      tone(c, 900 + Math.random() * 500, 0.03, {
        type: 'square',
        gain: 0.03,
        at: i * 0.045,
      });
    }
  },
  land: c => {
    noise(c, 0.05, 2600, 0.22, 0, 1.5);
    tone(c, 180 + Math.random() * 60, 0.07, {
      type: 'triangle',
      gain: 0.18,
      glideTo: 90,
    });
  },
  hold: c => tone(c, 1200, 0.05, { type: 'square', gain: 0.06, glideTo: 1600 }),
  unhold: c =>
    tone(c, 1000, 0.05, { type: 'square', gain: 0.06, glideTo: 700 }),
  click: c => tone(c, 800, 0.03, { type: 'square', gain: 0.05 }),
  turn: c => {
    tone(c, N.A4, 0.09, { type: 'triangle', gain: 0.12 });
    tone(c, N.E5, 0.14, { type: 'triangle', gain: 0.12, at: 0.08 });
  },
  score: c => {
    tone(c, N.C5, 0.12, { type: 'triangle', gain: 0.2 });
    tone(c, N.E5, 0.16, { type: 'triangle', gain: 0.2, at: 0.09 });
    tone(c, N.G5, 0.22, { type: 'sine', gain: 0.16, at: 0.18 });
  },
  scratch: c => {
    tone(c, 150, 0.25, { type: 'sawtooth', gain: 0.14, glideTo: 55 });
    noise(c, 0.12, 300, 0.12, 0, 0.7);
  },
  bonus: c => {
    [N.C5, N.E5, N.G5, N.C6].forEach((f, i) =>
      tone(c, f, 0.18, { type: 'triangle', gain: 0.18, at: i * 0.07 })
    );
  },
  jackpot: c => {
    const seq = [N.C5, N.E5, N.G5, N.C6, N.G5, N.C6, N.E6];
    seq.forEach((f, i) => {
      tone(c, f, 0.22, { type: 'square', gain: 0.12, at: i * 0.11 });
      tone(c, f / 2, 0.22, { type: 'triangle', gain: 0.12, at: i * 0.11 });
    });
    for (let i = 0; i < 10; i++)
      noise(c, 0.08, 4000 + Math.random() * 3000, 0.08, 0.5 + i * 0.06, 6);
    tone(c, N.C6, 0.9, { type: 'sine', gain: 0.14, at: 0.8 });
  },
  win: c => {
    [N.C4, N.E4, N.G4, N.C5, N.E5, N.G5, N.C6].forEach((f, i) =>
      tone(c, f, 0.3, { type: 'triangle', gain: 0.16, at: i * 0.09 })
    );
    tone(c, N.C6, 1.2, { type: 'sine', gain: 0.12, at: 0.65 });
  },
  lose: c => {
    [N.E5, N.D5, N.C5, N.A4].forEach((f, i) =>
      tone(c, f, 0.35, { type: 'triangle', gain: 0.14, at: i * 0.18 })
    );
  },
  power: c => {
    tone(c, 300, 0.35, { type: 'sawtooth', gain: 0.1, glideTo: 1800 });
    noise(c, 0.3, 1500, 0.1, 0, 0.5);
  },
};

export const sound = {
  /** Fire and forget. Safe before user gesture (silently no-ops). */
  play(id: SoundId): void {
    const vol = level();
    if (vol <= 0) return;
    const c = getCtx();
    if (!c || !master || c.state !== 'running') return;
    try {
      master.gain.setValueAtTime(vol, c.currentTime);
      PLAY[id](c);
    } catch {
      /* audio unavailable */
    }
  },
  /** Call from a user gesture to unlock the audio context. */
  unlock(): void {
    if (level() <= 0) return;
    const c = getCtx();
    if (!c) return;
    if (c.state === 'suspended') c.resume().catch(() => {});
  },
};
